import React, { useState } from 'react';
import Icon from '../../../components/openplay/OpenPlayIcon';
import BottomTabNavigation from '../../../components/openplay/BottomTabNavigation';
import MetricsCard from './MetricsCard';
import EventManagementCard from './EventManagementCard';
import ApplicationCard from './ApplicationCard';
import ActivityFeed from './ActivityFeed';
import AnalyticsSection from './AnalyticsSection';

const OrganizerDashboardInteractive = ({ data }) => {
  const [selectedTab, setSelectedTab] = useState('overview');

  return (
    <div className="min-h-screen bg-background pb-[80px] md:pb-0 md:pl-[240px]">
      <header className="sticky top-0 z-sticky bg-card shadow-elevation-1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">주최자 대시보드</h1>
              <p className="text-sm text-muted-foreground mt-1 caption">이벤트와 참가자를 효율적으로 관리하세요</p>
            </div>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:shadow-elevation-2 transition-smooth touch-target font-medium active:scale-97"
              type="button"
            >
              <Icon name="PlusIcon" size={20} />
              <span className="hidden md:inline">새 이벤트</span>
            </button>
          </div>

          <div className="flex gap-2 mt-4 overflow-x-auto">
            <button
              onClick={() => setSelectedTab('overview')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-smooth ${
                selectedTab === 'overview'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              type="button"
            >
              개요
            </button>
            <button
              onClick={() => setSelectedTab('events')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-smooth ${
                selectedTab === 'events'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              type="button"
            >
              이벤트 관리
            </button>
            <button
              onClick={() => setSelectedTab('applications')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-smooth ${
                selectedTab === 'applications'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
              type="button"
            >
              신청 관리
              {data.metrics.pendingApplications > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary-foreground text-primary">
                  {data.metrics.pendingApplications}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {selectedTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricsCard title="활성 이벤트" value={data.metrics.activeEvents} icon="CalendarDaysIcon" trend="+2" trendDirection="up" />
              <MetricsCard title="총 참가자" value={data.metrics.totalParticipants} icon="UserGroupIcon" trend="+12" trendDirection="up" />
              <MetricsCard title="마감 임박" value={data.metrics.upcomingDeadlines} icon="ClockIcon" variant="warning" />
              <MetricsCard title="참여율" value={`${data.metrics.engagementRate}%`} icon="ChartBarIcon" trend="+5.2%" trendDirection="up" />
              <MetricsCard title="대기 신청" value={data.metrics.pendingApplications} icon="DocumentTextIcon" variant="info" />
              <MetricsCard title="새 문의" value={data.metrics.newInquiries} icon="ChatBubbleLeftIcon" variant="info" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold font-heading text-foreground">최근 이벤트</h2>
                  <button className="text-sm text-primary hover:underline font-medium" type="button">
                    전체 보기
                  </button>
                </div>
                <div className="space-y-3">
                  {data.recentEvents.map((event) => (
                    <EventManagementCard key={event.id} event={event} />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold font-heading text-foreground">최근 활동</h2>
                <ActivityFeed activities={data.recentActivities} />
              </div>
            </div>

            <AnalyticsSection analytics={data.analytics} />
          </>
        )}

        {selectedTab === 'events' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold font-heading text-foreground">전체 이벤트</h2>
              <div className="flex gap-2">
                <select className="px-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm">
                  <option>전체 상태</option>
                  <option>진행중</option>
                  <option>모집완료</option>
                  <option>종료</option>
                </select>
                <select className="px-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm">
                  <option>전체 종목</option>
                  <option>축구</option>
                  <option>농구</option>
                  <option>테니스</option>
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {data.recentEvents.map((event) => (
                <EventManagementCard key={event.id} event={event} detailed />
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'applications' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold font-heading text-foreground">대기중인 신청</h2>
              <button className="text-sm text-primary hover:underline font-medium" type="button">
                일괄 처리
              </button>
            </div>
            <div className="space-y-3">
              {data.pendingApplications.map((application) => (
                <ApplicationCard key={application.id} application={application} />
              ))}
            </div>
          </div>
        )}
      </main>

      <BottomTabNavigation userRole="organizer" notificationCount={data.metrics.pendingApplications} />
    </div>
  );
};

export default OrganizerDashboardInteractive;
